import type {
  CreateDraftWithdrawalResponse,
  ExchangerPaymentMethodData,
  GetPublicBankResponse,
  PaymentMethodsVerificationStatusesEnum,
  PaymentMethodType,
  PaymentMethodTypeEnum,
} from '@fsd/shared/api';
import { CreateDraftWithdrawalRequest, PaymentMethodV2 } from '@fsd/shared/api';
import { useHttpClient } from '../../http/index';
import type {
  GetAccountsResponse,
  PaymentMethod,
  GetCurrenciesForWithdrawPaymentMethodRequest,
  GetCurrenciesForWithdrawPaymentMethodResponse,
  GetBankResponse,
  UploadPaymentMethodImagePayload,
  UploadPaymentMethodImageUrlData,
  CryptoCurrencyData,
  PaymentMethodEps,
  GetPaymentMethodsBankListResponse,
  PaymentSystemsResponse,
  ExchangeRateResponse,
  Send2FAResponse,
  Confirmation2FAResponse,
  UploadImagePaymentMethodResponse,
  RejectedImage,
} from './types';
import { handleImageUpload, uploadImages, conditionRejectedFile } from './utils';

const {
  get,
  post,
  put,
  delete: del,
} = useHttpClient();

export const useFinancesApi = () => {
  const getAccounts = async () => {
    const {
      data: { attributes: { realAccounts, wallets } },
    } = await get<GetAccountsResponse>('/v2/pc/accounts', {
      timeout: 90_000,
    });

    return { accounts: realAccounts, wallets };
  };

  const getPaymentMethods = async () => get<PaymentMethod[]>('/v1/payment_methods', {
    timeout: 90_000,
  });

  const getPaymentMethodTypes = async () => get<PaymentMethodType[]>('/v1/payment_methods/types');

  const createPaymentMethod = async (
    type: PaymentMethodTypeEnum,
    payload: Record<string, unknown>,
  ) => post<PaymentMethod>(`/v1/payment_methods/${type.replace('payment_method_', '')}`, {
    data: {
      type,
      attributes: payload,
    },
  }, {
  });

  const getPaymentMethodByUuid = async (uuid: string, type: PaymentMethodTypeEnum) => get<PaymentMethod>(`/v1/payment_methods/${type.replace('payment_method_', '')}/${uuid}`);
  const getPaymentMethodByUuidV2 = async (uuid: string, type: PaymentMethodTypeEnum) => get<PaymentMethodV2>(`/v2/pc/payment-methods/${type.replace('payment_method_', '')}/${uuid}`);

  const updatePaymentMethod = async (
    uuid: string,
    type: PaymentMethodTypeEnum,
    payload: Record<string, unknown>,
  ) => put<PaymentMethod>(`/v1/payment_methods/${type.replace('payment_method_', '')}/${uuid}`, {
    data: {
      type,
      id: uuid,
      attributes: payload,
    },
  });

  const getCurrenciesForWithdrawPaymentMethod = async (
    { type, uid, accountCurrency }: GetCurrenciesForWithdrawPaymentMethodRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await get<GetCurrenciesForWithdrawPaymentMethodResponse>(`/v2/pc/withdrawals/processing-currencies/${type}/${uid}/${accountCurrency}`,
      {
        signal,
      });

    return data;
  };

  const createDraftWithdrawal = async (payload: CreateDraftWithdrawalRequest) => {
    const { data } = await post<CreateDraftWithdrawalResponse>('/v3/pc/withdrawals/draft', payload, {
      errorHandlerOptions: {
        skipErrorByOneOfCodes: [400, 422],
      },
    });

    return data;
  };

  const confirmWithdrawal = async (withdrawalUid: string) => {
    await post<void>('/v3/pc/withdrawals', {
      data: {
        attributes: {
          withdrawalUid,
        },
      },
    });
  };

  const getBanksForWithdraw = async (withdrawalUid: string, signal?: AbortSignal) => {
    const { data } = await get<GetBankResponse>(`/v2/pc/banks/${withdrawalUid}`, {
      signal,
    });

    return { data };
  };

  const deletePaymentMethodCard = async (uuid: string) => {
    return del(`/v1/payment_methods/card/${uuid}`);
  };

  const deletePaymentMethodEps = async (uuid: string) => {
    return del(`/v1/payment_methods/eps/${uuid}`);
  };

  const deletePaymentMethodCrypto = async (uuid: string) => {
    return del(`/v1/payment_methods/crypto/${uuid}`);
  };

  const deletePaymentMethodBank = async (uuid: string) => {
    return del(`/v1/payment_methods/bank/${uuid}`);
  };

  const uploadImagePaymentMethod = async (
    uuid: string,
    type: string,
    images: File[],
  ): Promise<UploadImagePaymentMethodResponse> => {
    const uploadImagesUrl = `/v1/payment_methods/${type.replace('payment_method_', '')}/${uuid}/images/create_upload_urls`;
    const checksums = await Promise.all(
      images.map((imageData) => handleImageUpload(imageData)),
    );
    const imagesPayload = images.map((imageData, idx) => {
      return {
        type: 'payment_method_images',
        attributes: {
          checksum: checksums[idx],
          contentLength: imageData.size,
          filename: imageData.name,
          type: imageData.type,
          contentType: 'application/octet-stream',
          slotId: 'front-side',
        },
      };
    });
    const urls = await post<UploadPaymentMethodImagePayload>(uploadImagesUrl, { data: imagesPayload });
    const uploadUrls = images.map((file, index) => {
      return { ...urls.data[index], origin: file };
    });
    const uploadImagesPayload = images.map((file, index) => {
      return {
        ...urls.data[index].attributes,
        id: urls.data[index].id,
        type: urls.data[index].type,
        origin: file,
      };
    });
    const results = await Promise.allSettled(
      uploadImagesPayload.map((imagePayload) => uploadImages({ file: imagePayload.origin, params: imagePayload })),
    );
    const hasRejected = results.some((result) => conditionRejectedFile(result));
    const rejectedImages: RejectedImage[] = [];

    results.forEach((item, index) => {
      if (conditionRejectedFile(item)) {
        rejectedImages.push({
          isRejected: true,
          origin: images[index],
        });
      }
    });

    return { status: !hasRejected, rejectedImages, uploadUrls };
  };

  const reloadImageToAws = async (uploadUrlData: UploadPaymentMethodImageUrlData): Promise<boolean> => {
    const uploadImagePayload = {
      ...uploadUrlData.attributes,
      id: uploadUrlData.id,
      type: uploadUrlData.type,
      origin: uploadUrlData.origin!,
    };
    const results = await Promise.allSettled([
      uploadImages({ file: uploadImagePayload.origin, params: uploadImagePayload }),
    ]);
    const isRejected = results.some((result) => conditionRejectedFile(result));

    return !isRejected;
  };

  const changePaymentMethodVerification = async (
    uuid: string,
    type: string,
    verification: PaymentMethodsVerificationStatusesEnum,
  ) => {
    return post(`/v1/payment_methods/${type.replace('payment_method_', '')}/${uuid}/${verification}`, {
      data: { type, id: uuid },
    });
  };

  const getCryptoCurrencies = async () => get<{ data: CryptoCurrencyData[] }>('/v1/payment_methods/crypto/list');

  const getPaymentMethodsEpsList = async () => get<PaymentMethodEps[]>('/v1/payment_methods/eps/list');

  const getExchangersPaymentMethodsList = async () => get<{ data: ExchangerPaymentMethodData[] }>('v1/payment_methods/exchanger/list');
  const getPaymentMethodsBankList = async () => {
    const { data } = await get<GetPaymentMethodsBankListResponse>('/v1/payment_methods/bank/list');

    return data;
  };

  const getPublicBanks = async (region: string) => {
    const { data } = await get<GetPublicBankResponse>('/v1/pc/public-banks', {
      params: {
        region,
      },
    });

    return data;
  };

  const getPaymentSystems = async () => {
    const { data } = await get<PaymentSystemsResponse>('/v3/pc/payment-systems/withdrawals', {
      timeout: 90_000,
    });

    return data;
  };

  const getExchangeRate = async (pair: string) => {
    const { data } = await get<ExchangeRateResponse>(`/v1/exchange/rates/${pair}/latest`);
    return data;
  };

  const request2FATelegram = async () => post<Send2FAResponse>('/v2/pc/withdrawals/2fa/telegram');
  const request2FACall = async () => post<Send2FAResponse>('/v2/pc/withdrawals/2fa/call_password');
  const request2FASms = async () => post<Send2FAResponse>('/v2/pc/withdrawals/2fa/sms');
  const send2FA = async () => post<Send2FAResponse>('/v2/pc/withdrawals/2fa/default');
  const confirmation2FA = async (code: string) => post<Confirmation2FAResponse>('/v2/pc/withdrawals/2fa/confirmation', {
    data: { attributes: { code } },
  });

  return {
    getAccounts,
    getPaymentMethods,
    getPaymentMethodTypes,
    updatePaymentMethod,
    createPaymentMethod,
    getPaymentMethodByUuid,
    getPaymentMethodByUuidV2,
    getCurrenciesForWithdrawPaymentMethod,
    createDraftWithdrawal,
    getBanksForWithdraw,
    confirmWithdrawal,
    deletePaymentMethodCard,
    deletePaymentMethodEps,
    deletePaymentMethodCrypto,
    deletePaymentMethodBank,
    uploadImagePaymentMethod,
    reloadImageToAws,
    changePaymentMethodVerification,
    getCryptoCurrencies,
    getPaymentMethodsEpsList,
    getExchangersPaymentMethodsList,
    getPaymentMethodsBankList,
    getPublicBanks,
    getExchangeRate,
    getPaymentSystems,
    request2FATelegram,
    request2FACall,
    request2FASms,
    send2FA,
    confirmation2FA,
  };
};
